/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * Client-side loadout management - Weapon selection persistence and validation
 * loadout.service.ts - Service for managing player weapon loadout across storage layers
 */

import { WeaponType, isValidWeaponType } from "@game/config";
import {
  hasActiveRealtimeBackend,
  isActiveRealtimeBackendConnected,
  sendRealtimeCommand,
} from "../networking/realtime.runtime";

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * LocalStorage key for persisting weapon selection
 * Used for anonymous users and as fallback for logged-in users
 */
const STORAGE_KEY = "player_loadout" as const;

/**
 * Default weapon type when no valid loadout is found
 */
const DEFAULT_WEAPON = WeaponType.DEFAULT;

// ============================================================================
// ERROR TYPES
// ============================================================================

/**
 * Error thrown when weapon type validation fails
 * Indicates an invalid weapon type was provided
 */
export class InvalidWeaponTypeError extends Error {
  constructor(public readonly attemptedWeapon: string) {
    super(`Invalid weapon type: ${attemptedWeapon}`);
    this.name = "InvalidWeaponTypeError";
  }
}

/**
 * Error thrown when localStorage operations fail
 * Wraps underlying DOMException or QuotaExceededError
 */
export class StorageError extends Error {
  constructor(
    message: string,
    public readonly cause?: Error,
  ) {
    super(message);
    this.name = "StorageError";
  }
}

/**
 * Error thrown when database save operations fail
 * Wraps network or API errors
 */
export class DatabaseSaveError extends Error {
  constructor(
    message: string,
    public readonly cause?: Error,
  ) {
    super(message);
    this.name = "DatabaseSaveError";
  }
}

// ============================================================================
// LOADOUT SERVICE
// ============================================================================

/**
 * Client-side service for managing player weapon loadout
 *
 * **Storage Strategy:**
 * - Anonymous users: localStorage only (always works)
 * - Logged-in users: database primary, localStorage fallback
 *
 * **Validation:**
 * - All weapon types validated using isValidWeaponType()
 * - Invalid weapons rejected with InvalidWeaponTypeError
 * - Storage errors handled gracefully with fallback to defaults
 *
 * **Thread Safety:**
 * - Service is singleton, safe for concurrent access
 * - localStorage operations are synchronous and atomic
 * - Database operations use async/await with proper error handling
 *
 * @example
 * ```ts
 * // Load saved weapon on startup
 * const weapon = loadoutService.loadFromStorage();
 *
 * // Save weapon selection
 * loadoutService.saveToStorage(WeaponType.SHOTGUN);
 *
 * // Save to database (logged-in users)
 * await loadoutService.saveToDatabase(WeaponType.SNIPER);
 *
 * // Get current weapon
 * const current = loadoutService.getCurrentWeapon();
 * ```
 */
export class LoadoutService {
  /**
   * Currently selected weapon
   * Initialized to DEFAULT, updated by load/save operations
   */
  private currentWeapon: WeaponType = DEFAULT_WEAPON;
  private initialized = false;

  /**
   * Load weapon selection from localStorage
   *
   * **Behavior:**
   * - Reads from localStorage using STORAGE_KEY
   * - Validates stored value using isValidWeaponType()
   * - Returns DEFAULT_WEAPON if no valid value found
   * - Updates internal currentWeapon state
   *
   * **Error Handling:**
   * - Storage access errors logged and DEFAULT_WEAPON returned
   * - Invalid weapon types ignored and DEFAULT_WEAPON returned
   * - Never throws, always returns valid WeaponType
   *
   * @returns Valid WeaponType (stored weapon or DEFAULT_WEAPON)
   *
   * @example
   * ```ts
   * const weapon = loadoutService.loadFromStorage();
   * console.log(weapon); // WeaponType.SHOTGUN or WeaponType.DEFAULT
   * ```
   */
  public loadFromStorage(): WeaponType {
    try {
      this.initialized = true;
      const storedValue = localStorage.getItem(STORAGE_KEY);

      if (storedValue === null) {
        this.currentWeapon = DEFAULT_WEAPON;
        return this.currentWeapon;
      }

      if (isValidWeaponType(storedValue)) {
        this.currentWeapon = storedValue;
        return this.currentWeapon;
      }

      console.warn(
        `[LoadoutService] Invalid weapon type in storage: ${storedValue}, using default`,
      );
      this.currentWeapon = DEFAULT_WEAPON;
      return this.currentWeapon;
    } catch (error) {
      console.error(
        "[LoadoutService] Failed to load from localStorage:",
        error,
      );
      this.currentWeapon = DEFAULT_WEAPON;
      return this.currentWeapon;
    }
  }

  /**
   * Save weapon selection to localStorage
   *
   * **Behavior:**
   * - Validates weapon type before saving
   * - Throws InvalidWeaponTypeError if validation fails
   * - Updates internal currentWeapon state
   * - Persists to localStorage using STORAGE_KEY
   *
   * **Error Handling:**
   * - Invalid weapon types throw InvalidWeaponTypeError
   * - Storage quota errors throw StorageError
   * - State updated only after successful validation
   *
   * @param weapon - Weapon type to save (must be valid WeaponType)
   * @throws {InvalidWeaponTypeError} If weapon type is invalid
   * @throws {StorageError} If localStorage write fails
   *
   * @example
   * ```ts
   * loadoutService.saveToStorage(WeaponType.SHOTGUN);
   * // Throws if weapon invalid or storage full
   * ```
   */
  public saveToStorage(weapon: WeaponType): void {
    if (!isValidWeaponType(weapon)) {
      throw new InvalidWeaponTypeError(weapon);
    }

    try {
      this.currentWeapon = weapon;
      localStorage.setItem(STORAGE_KEY, weapon);
    } catch (error) {
      const storageError = new StorageError(
        `Failed to save weapon to localStorage: ${error instanceof Error ? error.message : String(error)}`,
        error instanceof Error ? error : undefined,
      );
      throw storageError;
    }
  }

  /**
   * Save weapon selection to database
   *
   * **Behavior:**
   * - Validates weapon type before saving
   * - Sends `set_loadout` realtime command when backend is connected
   * - Falls back to localStorage on success or failure
   * - Updates internal currentWeapon state
   *
   * **Realtime Protocol:**
   * - Command: `{ type: "set_loadout", primaryWeapon }`
   * - Dispatched only when active realtime backend is connected
   *
   * **Error Handling:**
   * - Invalid weapon types throw InvalidWeaponTypeError
   * - Realtime command errors throw DatabaseSaveError
   * - Always falls back to localStorage (even on error)
   * - localStorage errors propagate as StorageError
   *
   * @param weapon - Weapon type to save (must be valid WeaponType)
   * @throws {InvalidWeaponTypeError} If weapon type is invalid
   * @throws {DatabaseSaveError} If database save fails
   * @throws {StorageError} If localStorage fallback fails
   *
   * @example
   * ```ts
   * try {
   *   await loadoutService.saveToDatabase(WeaponType.SNIPER);
   * } catch (error) {
   *   if (error instanceof DatabaseSaveError) {
   *     // Database failed, but localStorage succeeded
   *     console.warn("Database save failed, using localStorage");
   *   }
   * }
   * ```
   */
  public async saveToDatabase(weapon: WeaponType): Promise<void> {
    if (!isValidWeaponType(weapon)) {
      throw new InvalidWeaponTypeError(weapon);
    }

    let databaseError: DatabaseSaveError | null = null;
    try {
      if (hasActiveRealtimeBackend() && isActiveRealtimeBackendConnected()) {
        await sendRealtimeCommand({
          type: "set_loadout",
          primaryWeapon: weapon,
        });
      }
    } catch (error) {
      databaseError = new DatabaseSaveError(
        `Failed to save weapon to database: ${error instanceof Error ? error.message : String(error)}`,
        error instanceof Error ? error : undefined,
      );
      console.error("[LoadoutService] Database save failed:", databaseError);
    } finally {
      this.saveToStorage(weapon);
    }

    if (databaseError) {
      throw databaseError;
    }
  }

  /**
   * Get current weapon selection
   *
   * **Behavior:**
   * - Returns current weapon from internal state
   * - Initialized to DEFAULT_WEAPON
   * - Updated by loadFromStorage() and save methods
   *
   * **Usage:**
   * - Call after loadFromStorage() on startup
   * - Call to get weapon for game initialization
   * - Always returns valid WeaponType (never null)
   *
   * @returns Current weapon type (never null, defaults to DEFAULT_WEAPON)
   *
   * @example
   * ```ts
   * loadoutService.loadFromStorage();
   * const weapon = loadoutService.getCurrentWeapon();
   * player.setWeapon(weapon);
   * ```
   */
  public getCurrentWeapon(): WeaponType {
    // Lazy initialize from storage on first access to avoid race with UI mount
    if (!this.initialized) {
      try {
        this.loadFromStorage();
      } catch (_) {
        // Ignore and keep default
      }
    }
    return this.currentWeapon;
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

/**
 * Global singleton instance of LoadoutService
 * Use this instance throughout the application for loadout management
 *
 * @example
 * ```ts
 * import { loadoutService } from './loadout/loadout.service';
 *
 * // Load on startup
 * loadoutService.loadFromStorage();
 *
 * // Save weapon selection
 * loadoutService.saveToStorage(WeaponType.SHOTGUN);
 * ```
 */
export const loadoutService = new LoadoutService();
